import React from "react";

import { useLocalization } from "@fluent/react";
import { Table } from "@narmi/design_system";

import { formatReceiptAmount, formatReceiptDate } from "../format";
import { getReasonCopy } from "../utils/reasonCopy";

import styles from "./ReceiptsTable.module.scss";

import type { ReceiptListRow } from "../hooks/useReceiptsList";

const HEADER_LABEL_CLASS =
  "nds-typescale--upper fontColor--primary fontWeight--bold fontSize--s";

interface UnmatchedTableProps {
  rows: ReceiptListRow[];
  onRowClick?: (uuid: string) => void;
  // Injected for testability; defaults to wall-clock at render time.
  now?: Date;
}

const UnmatchedTable = ({ rows, onRowClick, now }: UnmatchedTableProps) => {
  const { l10n } = useLocalization();
  const resolvedNow = now ?? new Date();

  return (
    <div
      className={styles["receipts-table-wrapper"]}
      data-testid="receipts-unmatched-table"
    >
      <Table
        colVisibility={["*", "m", "m"]}
        colLayout={{
          s: "1fr",
          m: "1fr 1fr auto",
          l: "1fr 1fr auto",
        }}
        rowDensity="compact"
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-unmatched-table-receipt")}
              </span>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-unmatched-table-reason")}
              </span>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-unmatched-table-date")}
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row) => {
            const reasonCopy = getReasonCopy(row, resolvedNow, l10n);
            const isExtractionFailed = row.status === "extraction_failed";
            const shortDate = formatReceiptDate(row.transaction_date);
            const amount = formatReceiptAmount(row.amount);
            // For EXTRACTION_FAILED rows, render explicit placeholders for any
            // field the pipeline couldn't extract so the row never collapses to
            // a blank cell. Other statuses filter empties out as before.
            const merchantText =
              row.merchant ??
              l10n.getString(
                isExtractionFailed
                  ? "receipts-unmatched-no-merchant"
                  : "receipts-table-unknown-merchant"
              );
            const secondaryParts = isExtractionFailed
              ? [
                  shortDate || l10n.getString("receipts-unmatched-no-date"),
                  amount || l10n.getString("receipts-unmatched-no-amount"),
                ]
              : [shortDate, amount].filter(Boolean);

            return (
              <Table.Row
                key={row.uuid}
                onRowClick={onRowClick ? () => onRowClick(row.uuid) : undefined}
              >
                <Table.Cell>
                  <div
                    className={styles["stacked-cell"]}
                    data-testid={`receipts-unmatched-row-${row.uuid}`}
                  >
                    <span className="fontColor--primary fontWeight--semibold fontSize--s">
                      {merchantText}
                    </span>
                    {secondaryParts.length > 0 && (
                      <span className="fontColor--secondary fontSize--s">
                        {secondaryParts.join(" · ")}
                      </span>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="fontColor--primary fontSize--s">
                    {reasonCopy}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="fontColor--secondary fontSize--s">
                    {formatReceiptDate(row.transaction_date)}
                  </span>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

export default UnmatchedTable;
